import React, { useRef, useMemo, useEffect } from 'react';
import { Button } from '../../../../../base/components/Button';
import { CityAutocomplete } from '../../../../../base/components/CityAutocomplete';
import { ErrorMessage } from '../../../../../base/components/Forms/ErrorMessage';
import { Input } from '../../../../../base/components/Forms/Input';
import { PhoneInput } from '../../../../../base/components/Forms/PhoneInput';
import { RichTextEditor } from '../../../../../base/components/Forms/RichTextEditor';
import { Icon } from '../../../../../base/components/Icon';
import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';
import { City } from '../../../../../base/interfaces/config';
import { getRefById, pluralize, RefMap, scrollToError } from '../../../../../base/utils';
import { __t } from '../../../../../base/utils/t';
import { recruiterAppActions } from '../../../../redux/slices/recruiterAppSlice';
import { profileActions } from '../../redux/slice/profileSlice';
import { RecruiterType } from '../../../../enums';
import { LogoUpload } from '../LogoUpload';
import { RecruiterProfileAreasSelect } from '../ProfileAreasSelect';
import { RecruiterProfileCountriesSelect } from '../ProfileCountriesSelect';

import './styles.scss';

export const RecruiterProfileForm = () => {
    const dispatch = useAppDispatch();
    const {
        recruiterAppProfile: {
            fields: { name, phone, agencyName, website, description, city, countries, areas, type },
            errors,
            shouldScrollToError,
        },
        recruiterApp: {
            config: { phoneInputConfig },
            citySearchOptions,
        },
    } = useAppSelector((state) => state);

    const refMap: RefMap[] = [
        {
            id: 'agencyName',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'password',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'phone',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'contactName',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'website',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'description',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(profileActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const inputChangeHandler = (id: string, value: unknown) => {
        dispatch(profileActions.setField({ id, value }));
    };

    const mergedPhoneNameErrors = useMemo(() => {
        let result: string[] = [];
        if (errors?.name) {
            result = [...result, ...errors.name];
        }
        if (errors?.phone) {
            result = [...result, ...errors.phone];
        }
        return result;
    }, [errors]);

    const submitProfile = () => dispatch(profileActions.submitProfile(callback));

    const callback = () => window.scrollTo({ top: 0, left: 0 });

    const pluralizedCountryCountTexts = [__t('страна'), __t('страны'), __t('стран')];
    const pluralizedAreaCountTexts = [__t('сфера'), __t('сферы'), __t('сфер')];

    return (
        <>
            <div className='recruiter-profile-form'>
                <div
                    className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('logo', refMap)?.ref}
                >
                    <LogoUpload />
                </div>
                <div
                    className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('agencyName', refMap)?.ref}
                >
                    <p className='recruiter-profile-form__form-label'>{type === RecruiterType.Individual ?  __t('Имя и фамилия') :  __t('Название компании')}</p>
                    <Input
                        value={agencyName}
                        type='text'
                        placeholder={type === RecruiterType.Individual ? __t('Введите ваше имя') : __t('Введите название компании')}
                        onChange={(value: string) => inputChangeHandler('agencyName', value)}
                        errors={errors?.agencyName}
                        tabIndex={1}
                    />
                </div>

                <div
                    className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('name', refMap)?.ref}
                >
                    <p className='recruiter-profile-form__form-label'>{__t('В каком городе находитесь?')}</p>
                    <CityAutocomplete
                        appType='worker'
                        options={citySearchOptions}
                        selectedOption={city}
                        errors={errors?.cityId}
                        onSearch={(value: string) => dispatch(recruiterAppActions.citySearch(value))}
                        setSearchOptions={(options: City[] | null) =>
                            dispatch(recruiterAppActions.setCitySearch(options))
                        }
                        onSelect={(value: City | null) =>
                            dispatch(profileActions.setField({ id: 'city', value, errorGroupName: 'cityId' }))
                        }
                        tabIndex={2}
                    />
                </div>

                <div
                    className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('phone', refMap)?.ref}
                >
                    <div
                        className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                        ref={getRefById('name', refMap)?.ref}
                    >
                        <p className='recruiter-profile-form__form-label'>{__t('Контактное лицо')}</p>
                        <Input
                            value={name}
                            placeholder={__t('Введите имя')}
                            onChange={(value: string) => inputChangeHandler('name', value)}
                            removeBorderRadius='bottom'
                            errors={errors?.name}
                            hideErrorMessage={true}
                            tabIndex={3}
                        />
                        <PhoneInput
                            value={phone}
                            onChange={(value: string) => inputChangeHandler('phone', value)}
                            removeBorderRadius='top'
                            errors={errors?.phone}
                            hideErrorMessage={true}
                            onlyCountries={phoneInputConfig.countries}
                            preferredCountries={phoneInputConfig.preferredCountries}
                            localization={phoneInputConfig.localization}
                            tabIndex={4}
                        />
                        <ErrorMessage errors={mergedPhoneNameErrors} />
                    </div>
                </div>

                <div
                    className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('website', refMap)?.ref}
                >
                    <p className='recruiter-profile-form__form-label'>{__t('Веб-сайт')}</p>
                    <Input
                        value={website || ''}
                        type='text'
                        placeholder={__t('Введите адрес')}
                        onChange={(value: string) => inputChangeHandler('website', value)}
                        errors={errors?.website}
                        tabIndex={5}
                    />
                </div>

                <div className='recruiter-profile-form__settings-block recruiter-profile-form__form-item'>
                    <p className='recruiter-profile-form__settings-block-title'>{__t('Трудоустраиваем в ')}</p>
                    <div className='recruiter-profile-form__settings-item'>
                        <div className='recruiter-profile-form__settings-title'>
                            <div className='recruiter-profile-form__settings-icon flag flag_icon_eu'></div>
                            {countries.length
                                ? `${countries.length} ${pluralize(countries.length, pluralizedCountryCountTexts)}`
                                : __t('Все страны')}
                        </div>
                        <RecruiterProfileCountriesSelect>
                            <div className='recruiter-profile-form__settings-link' tabIndex={6}>
                                {__t('Редактировать')}
                            </div>
                        </RecruiterProfileCountriesSelect>
                    </div>
                    <div className='recruiter-profile-form__settings-divider'></div>
                    <div className='recruiter-profile-form__settings-item'>
                        <div className='recruiter-profile-form__settings-title'>
                            <Icon
                                icon='user-helmet-safety-light'
                                className='recruiter-profile-form__settings-icon'
                                size='24px'
                            />
                            {areas.length
                                ? `${areas.length} ${pluralize(areas.length, pluralizedAreaCountTexts)}`
                                : __t('Все сферы')}
                        </div>
                        <RecruiterProfileAreasSelect>
                            <div className='recruiter-profile-form__settings-link' tabIndex={7}>
                                {__t('Редактировать')}
                            </div>
                        </RecruiterProfileAreasSelect>
                    </div>
                </div>

                <div
                    className='recruiter-profile-form__form-item scroll-margin-for-fixed-header'
                    ref={getRefById('description', refMap)?.ref}
                >
                    <p className='recruiter-profile-form__form-label'>{__t('Составьте описание своей деятельности')}</p>
                    <RichTextEditor
                        initialValue={description || ''}
                        onChange={(value: string) => inputChangeHandler('description', value)}
                        placeholder={__t(
                            'К примеру, «Международное рекрутинговое агентство нового поколения. Наша специализация - поиск, адаптация и развитие персонала. Закрываем даже самые сложные вакансии...»',
                        )}
                        maxLength={2000}
                        tabIndex={8}
                    />
                </div>

                <Button className='recruiter-profile-form__button' onClick={submitProfile}>
                    {__t('Сохранить')}
                </Button>
            </div>
        </>
    );
};
